import React, { useState, useEffect } from 'react';
import { useRole } from '../context/RoleContext';
import { useChat } from '../context/ChatContext';
import { 
  ChevronRight, 
  ChevronDown, 
  X,
  Circle,
  CheckCircle2,
  MessageSquare,
  Plus,
  Clock,
  Bot,
  BookOpen,
  Loader2
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Subject metadata (icons and names)
const subjectMeta = {
  'filosofia': {
    title: 'Filosofía',
    iconUrl: 'https://customer-assets.emergentagent.com/job_elegant-assistant-1/artifacts/srpm4yza_milo_filosofia.png'
  },
  'historia-arte': {
    title: 'Historia del Arte',
    iconUrl: 'https://customer-assets.emergentagent.com/job_elegant-assistant-1/artifacts/gnhmb7ta_milo_historia_arte.png'
  },
  'cultura-audiovisual': {
    title: 'Cultura Audiovisual',
    iconUrl: 'https://customer-assets.emergentagent.com/job_elegant-assistant-1/artifacts/r6hvoxa7_milo_cultura_audiovisual.png'
  }
};

// Helper function to clean topic titles (removes garbage text but preserves length)
const cleanTopicTitle = (title) => {
  if (!title) return '';
  
  let cleaned = title
    // Remove garbage text patterns like "(marciano)", "(marcian", "(marc", etc.
    .replace(/\s*\([^)]*marcian[^)]*\)?/gi, '')
    .replace(/\s*\(marc[^)]*$/gi, '')
    .replace(/\s*\([^)]{0,10}$/g, '')  // Remove any incomplete parenthesis at end
    // Remove incomplete sentences that start after the main topic
    // Pattern: "Main Topic Secondary Topic incomplete text..."
    // We want to keep "Main Topic" or "Main Topic Secondary Topic" but remove fragments
    .replace(/\s+Cuando\s+el\s+\w*$/gi, '')  // Remove "Cuando el m..."
    .replace(/\s+Hablar\s+de\s+(los|las|el|la)?\s*\w*$/gi, '')  // Remove "Hablar de los..."
    .replace(/\s+La\s+fotograf[íi]a?\s*$/gi, '')  // Remove "La fotografía..."
    .replace(/\s+[A-Z][a-záéíóú]{0,2}$/g, '')  // Remove very short trailing words
    .trim();
  
  // Clean up any double spaces
  cleaned = cleaned.replace(/\s+/g, ' ');
  
  return cleaned;
};

// Topic Item Component - Now clickable to select subject and start chat with topic
const TopicItem = ({ topic, level = 0, isProfessor, subjectId, onSelectSubject, onStartTopicChat, isSubjectSelected }) => {
  const [isExpanded, setIsExpanded] = useState(level === 0);
  const hasSubtopics = topic.subtopics && topic.subtopics.length > 0;

  const handleClick = () => {
    // Always select the correct subject/agent when clicking any topic
    onSelectSubject(subjectId);
    
    if (hasSubtopics) {
      // For topics with subtopics, just expand/collapse
      setIsExpanded(!isExpanded);
    } else {
      // For leaf topics, also start a chat with a question about this topic
      if (onStartTopicChat) {
        // Use cleaned title for the chat message
        onStartTopicChat(cleanTopicTitle(topic.title), subjectId);
      }
    }
  };

  return (
    <div className="select-none">
      <div 
        className={`flex items-center gap-2 py-1.5 px-2 rounded-lg cursor-pointer transition-colors ${
          isProfessor ? 'hover:bg-white/10' : 'hover:bg-amber-50'
        }`}
        style={{ paddingLeft: `${8 + level * 16}px` }}
        onClick={handleClick}
        data-testid={`topic-item-${topic.id}`}
      >
        {hasSubtopics ? (
          <span className={`${isProfessor ? 'text-gray-400' : 'text-gray-500'}`}>
            {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </span>
        ) : (
          <span className="w-4 h-4 flex items-center justify-center">
            <Circle className={`w-3 h-3 ${isProfessor ? 'text-gray-600' : 'text-gray-300'}`} />
          </span>
        )}
        <span 
          className={`text-sm flex-1 line-clamp-2 ${
            isProfessor ? 'text-gray-300' : 'text-gray-700'
          } ${level === 0 ? 'font-medium' : ''}`}
          title={topic.title}
        >
          {cleanTopicTitle(topic.title)}
        </span>
        {hasSubtopics && (
          <span className={`text-xs ${isProfessor ? 'text-gray-500' : 'text-gray-400'}`}>
            {topic.subtopics.length}
          </span>
        )}
      </div>
      
      {hasSubtopics && isExpanded && (
        <div className="ml-2">
          {topic.subtopics.map(subtopic => (
            <TopicItem 
              key={subtopic.id} 
              topic={subtopic} 
              level={level + 1}
              isProfessor={isProfessor}
              subjectId={subjectId}
              onSelectSubject={onSelectSubject}
              onStartTopicChat={onStartTopicChat}
              isSubjectSelected={isSubjectSelected}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Subject Section Component - Header is now clickable to select subject
const SubjectSection = ({ subject, isProfessor, onSelectSubject, onStartTopicChat, isSelected }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  
  const totalTopics = subject.topics ? subject.topics.length : 0;

  const handleHeaderClick = () => {
    // Select subject and toggle expand
    onSelectSubject(subject.subjectId);
  };

  const handleExpandClick = (e) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  return (
    <div className={`mb-4 rounded-xl overflow-hidden transition-all ${
      isSelected
        ? isProfessor 
          ? 'bg-blue-500/10 ring-1 ring-blue-500/30' 
          : 'bg-amber-50 ring-1 ring-amber-200'
        : isProfessor ? 'bg-white/5' : 'bg-white'
    }`}>
      <div 
        className={`flex items-center gap-3 p-3 cursor-pointer transition-colors ${
          isProfessor ? 'hover:bg-white/5' : 'hover:bg-gray-50'
        }`}
        onClick={handleHeaderClick}
      >
        <img 
          src={subject.iconUrl} 
          alt={subject.title}
          className="w-[42px] h-[42px] object-contain"
        />
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className={`text-sm font-semibold ${isProfessor ? 'text-white' : 'text-gray-900'}`}>
              {subject.title}
            </h3>
            {isSelected && (
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                isProfessor ? 'bg-blue-500 text-white' : 'bg-amber-500 text-white'
              }`}>
                Activo
              </span>
            )}
          </div>
          <div className={`text-xs mt-1 ${isProfessor ? 'text-gray-400' : 'text-gray-500'}`}>
            {totalTopics > 0 ? `${totalTopics} temas` : 'Sin temas'}
          </div>
        </div>
        <button
          onClick={handleExpandClick}
          className={`p-1 rounded-full ${isProfessor ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}
        >
          <span className={isProfessor ? 'text-gray-400' : 'text-gray-500'}>
            {isExpanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
          </span>
        </button>
      </div>
      
      {isExpanded && subject.topics && subject.topics.length > 0 && (
        <div className={`px-2 pb-2 ${isProfessor ? 'border-t border-white/5' : 'border-t border-gray-100'}`}>
          {subject.topics.map(topic => (
            <TopicItem 
              key={topic.id} 
              topic={topic}
              isProfessor={isProfessor}
              subjectId={subject.subjectId}
              onSelectSubject={onSelectSubject}
              onStartTopicChat={onStartTopicChat}
              isSubjectSelected={isSelected}
            />
          ))}
        </div>
      )}
      
      {isExpanded && (!subject.topics || subject.topics.length === 0) && (
        <div className={`px-4 py-3 text-center ${isProfessor ? 'text-gray-500' : 'text-gray-400'}`}>
          <BookOpen className="w-5 h-5 mx-auto mb-1 opacity-50" />
          <p className="text-xs">No hay temas disponibles</p>
          <p className="text-[10px] opacity-70">Sube un documento de conocimiento</p>
        </div>
      )}
    </div>
  );
};

// Conversation Item Component - Now shows agent indicator
const ConversationItem = ({ conversation, isActive, onClick, isProfessor, subjects }) => {
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Ahora';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;
    return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
  };

  // Get agent name from subject_id
  const getAgentName = (subjectId) => {
    if (subjectId === 'default') return 'Milo';
    const subject = subjects?.find(s => s.id === subjectId);
    if (subject) return subject.name;
    // Fallback names
    const fallbackNames = {
      'filosofia': 'Filosofía',
      'historia-arte': 'Historia del Arte',
      'cultura-audiovisual': 'Cultura Audiovisual'
    };
    return fallbackNames[subjectId] || subjectId;
  };

  // Get agent color for the badge
  const getAgentColor = (subjectId) => {
    const colors = {
      'default': isProfessor ? 'bg-gray-600/50 text-gray-300' : 'bg-gray-100 text-gray-600',
      'filosofia': isProfessor ? 'bg-purple-500/20 text-purple-300' : 'bg-purple-50 text-purple-700',
      'historia-arte': isProfessor ? 'bg-amber-500/20 text-amber-300' : 'bg-amber-50 text-amber-700',
      'cultura-audiovisual': isProfessor ? 'bg-cyan-500/20 text-cyan-300' : 'bg-cyan-50 text-cyan-700'
    };
    return colors[subjectId] || colors['default'];
  };

  return (
    <button
      onClick={onClick}
      className={`w-full text-left p-3 rounded-xl transition-all ${
        isActive
          ? isProfessor
            ? 'bg-blue-500/20 border border-blue-500/30'
            : 'bg-amber-50 border border-amber-200'
          : isProfessor
            ? 'hover:bg-white/5 border border-transparent'
            : 'hover:bg-gray-50 border border-transparent'
      }`}
      data-testid={`conversation-${conversation.id}`}
    >
      <div className="flex items-start gap-2">
        <MessageSquare className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
          isActive
            ? isProfessor ? 'text-blue-400' : 'text-amber-600'
            : isProfessor ? 'text-gray-500' : 'text-gray-400'
        }`} />
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium truncate ${
            isProfessor ? 'text-white' : 'text-gray-800'
          }`}>
            {conversation.title || 'Nueva conversación'}
          </p>
          <div className="flex items-center gap-2 mt-1">
            {/* Agent Badge - Discrete indicator */}
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${getAgentColor(conversation.subject_id)}`}>
              {getAgentName(conversation.subject_id)}
            </span>
            {/* Time */}
            <div className={`flex items-center gap-1 text-[10px] ${
              isProfessor ? 'text-gray-500' : 'text-gray-400'
            }`}>
              <Clock className="w-2.5 h-2.5" />
              <span>{formatDate(conversation.updated_at)}</span>
            </div>
          </div>
        </div>
      </div>
    </button>
  );
};

const RightHistoryPanel = ({ isOpen, onClose }) => {
  const { isProfessor } = useRole();
  const { 
    subjects,
    conversations, 
    currentConversation, 
    selectConversation, 
    startNewChat,
    selectedSubject,
    setSelectedSubject,
    fetchAllConversations,
    setSelectedTopic,
    sendMessage
  } = useChat();
  const [activeTab, setActiveTab] = useState('plan'); // 'plan' or 'conversations' - plan is default
  const [allConversations, setAllConversations] = useState([]);
  const [isLoadingConversations, setIsLoadingConversations] = useState(false);
  const [learningPlans, setLearningPlans] = useState({});
  const [isLoadingPlans, setIsLoadingPlans] = useState(false);

  const panelTitle = isProfessor ? 'Plan de Enseñanza' : 'Plan de Aprendizaje';

  // Load learning plans from API when component mounts or when subjects change
  useEffect(() => {
    loadLearningPlans();
  }, [subjects]);

  const loadLearningPlans = async () => {
    setIsLoadingPlans(true);
    const plans = {};
    const subjectIds = ['filosofia', 'historia-arte', 'cultura-audiovisual'];
    
    for (const subjectId of subjectIds) {
      try {
        const response = await fetch(`${API_BASE}/api/learning-plan/${subjectId}`);
        if (response.ok) {
          const data = await response.json();
          plans[subjectId] = data.outline || [];
        }
      } catch (error) {
        console.error(`Error loading learning plan for ${subjectId}:`, error);
        plans[subjectId] = [];
      }
    }
    
    setLearningPlans(plans);
    setIsLoadingPlans(false);
  };

  // Helper to get subject name from API data or fallback
  const getSubjectDisplayName = (subjectId) => {
    // First try to get from subjects prop (from API)
    const subject = subjects?.find(s => s.id === subjectId);
    if (subject) return subject.name;
    // Fallback to static metadata
    return subjectMeta[subjectId]?.title || subjectId;
  };

  // Build learning plan data from loaded plans
  const learningPlanData = ['filosofia', 'historia-arte', 'cultura-audiovisual'].map(subjectId => ({
    id: subjectId,
    subjectId: subjectId,
    title: getSubjectDisplayName(subjectId),
    iconUrl: subjectMeta[subjectId]?.iconUrl || '',
    topics: learningPlans[subjectId] || []
  }));

  // Load all conversations when switching to conversations tab
  useEffect(() => {
    if (activeTab === 'conversations') {
      loadAllConversations();
    }
  }, [activeTab, isProfessor]);

  // Refresh conversations when a new message is sent (conversations list changes)
  useEffect(() => {
    if (activeTab === 'conversations' && conversations.length > 0) {
      loadAllConversations();
    }
  }, [conversations.length]);

  const loadAllConversations = async () => {
    setIsLoadingConversations(true);
    const data = await fetchAllConversations();
    setAllConversations(data);
    setIsLoadingConversations(false);
  };

  // Handle subject selection from learning plan
  const handleSelectSubject = (subjectId) => {
    const subject = subjects.find(s => s.id === subjectId);
    if (subject) {
      setSelectedSubject(subject);
      startNewChat(); // Start fresh conversation with new agent
    }
  };

  // Handle topic chat - select subject and send initial question
  const handleStartTopicChat = (topicTitle, subjectId) => {
    const subject = subjects.find(s => s.id === subjectId);
    if (subject) {
      setSelectedSubject(subject);
      startNewChat();
      // Send a question about this topic with the correct subjectId
      // Pass subjectId directly to avoid race condition with state update
      setTimeout(() => {
        sendMessage(`Explícame todo sobre: ${topicTitle}`, true, subjectId);
      }, 100);
    }
  };

  // Handle conversation selection - also set the correct subject
  const handleSelectConversation = (conv) => {
    // Find and set the subject for this conversation
    const subject = subjects.find(s => s.id === conv.subject_id);
    if (subject && (!selectedSubject || selectedSubject.id !== subject.id)) {
      setSelectedSubject(subject);
    }
    selectConversation(conv);
  };

  return (
    <aside 
      className={`right-panel ${isOpen ? 'open' : ''} ${isProfessor ? 'right-panel-dark' : ''}`} 
      data-testid="right-panel"
    >
      {/* Close button for tablet/mobile */}
      <button
        className={`absolute top-4 right-4 xl:hidden p-2 rounded-full ${
          isProfessor ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
        }`}
        onClick={onClose}
        aria-label="Close panel"
      >
        <X className={`w-5 h-5 ${isProfessor ? 'text-gray-400' : 'text-gray-500'}`} />
      </button>

      {/* Tabs - Plan de Aprendizaje on the left (first) */}
      <div className={`flex gap-1 p-1 rounded-lg mb-4 ${
        isProfessor ? 'bg-gray-800' : 'bg-gray-100'
      }`}>
        <button
          onClick={() => setActiveTab('plan')}
          className={`flex-1 px-3 py-2 text-xs font-medium rounded-md transition-colors ${
            activeTab === 'plan'
              ? isProfessor 
                ? 'bg-gray-700 text-white' 
                : 'bg-white text-gray-800 shadow-sm'
              : isProfessor
                ? 'text-gray-400 hover:text-white'
                : 'text-gray-500 hover:text-gray-700'
          }`}
          data-testid="tab-plan"
        >
          {panelTitle}
        </button>
        <button
          onClick={() => setActiveTab('conversations')}
          className={`flex-1 px-3 py-2 text-xs font-medium rounded-md transition-colors ${
            activeTab === 'conversations'
              ? isProfessor 
                ? 'bg-gray-700 text-white' 
                : 'bg-white text-gray-800 shadow-sm'
              : isProfessor
                ? 'text-gray-400 hover:text-white'
                : 'text-gray-500 hover:text-gray-700'
          }`}
          data-testid="tab-conversations"
        >
          Conversaciones
        </button>
      </div>

      {activeTab === 'conversations' ? (
        <>
          {/* New Chat Button - Lighter/thinner design */}
          <button
            onClick={startNewChat}
            className={`w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg mb-4 transition-colors border ${
              isProfessor
                ? 'border-gray-600 hover:bg-gray-800 text-gray-300 hover:text-white'
                : 'border-gray-200 hover:bg-gray-50 text-gray-600 hover:text-gray-800'
            }`}
            data-testid="new-chat-btn"
          >
            <Plus className="w-4 h-4" />
            <span className="text-sm">Nuevo chat</span>
          </button>

          {/* Conversations List - Shows ALL conversations from ALL agents */}
          <div className="flex-1 overflow-y-auto -mx-2 px-2 space-y-2" data-testid="conversations-list">
            {isLoadingConversations ? (
              <div className={`text-center py-8 ${
                isProfessor ? 'text-gray-500' : 'text-gray-400'
              }`}>
                <div className="w-6 h-6 border-2 border-current border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                <p className="text-sm">Cargando conversaciones...</p>
              </div>
            ) : allConversations.length === 0 ? (
              <div className={`text-center py-8 ${
                isProfessor ? 'text-gray-500' : 'text-gray-400'
              }`}>
                <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No hay conversaciones aún</p>
                <p className="text-xs mt-1">Inicia un chat para comenzar</p>
              </div>
            ) : (
              allConversations.map(conv => (
                <ConversationItem
                  key={conv.id}
                  conversation={conv}
                  isActive={currentConversation?.id === conv.id}
                  onClick={() => handleSelectConversation(conv)}
                  isProfessor={isProfessor}
                  subjects={subjects}
                />
              ))
            )}
          </div>
        </>
      ) : (
        <>
          {/* Header */}
          <div className="mb-4">
            <h2 
              className={`text-lg font-semibold ${isProfessor ? 'text-white' : 'text-gray-900'}`} 
              data-testid="panel-title"
            >
              {panelTitle}
            </h2>
            <p className={`text-xs mt-1 ${isProfessor ? 'text-gray-400' : 'text-gray-500'}`}>
              Selecciona un tema para iniciar una conversación
            </p>
          </div>

          {/* Subjects List */}
          <div className="flex-1 overflow-y-auto -mx-2 px-2" data-testid="subjects-list">
            {isLoadingPlans ? (
              <div className={`text-center py-8 ${isProfessor ? 'text-gray-500' : 'text-gray-400'}`}>
                <Loader2 className="w-6 h-6 mx-auto mb-2 animate-spin" />
                <p className="text-sm">Cargando plan de estudio...</p>
              </div>
            ) : (
              learningPlanData.map(subject => (
                <SubjectSection 
                  key={subject.id} 
                  subject={subject}
                  isProfessor={isProfessor}
                  onSelectSubject={handleSelectSubject}
                  onStartTopicChat={handleStartTopicChat}
                  isSelected={selectedSubject?.id === subject.subjectId}
                />
              ))
            )}
          </div>
        </>
      )}
    </aside>
  );
};

export default RightHistoryPanel;
